<template>
    <div class="selected-sort-container">
        <div class="export-modal-title">
            已选字段（共{{ props.selectedCount }}个）
        </div>
        <a-collapse ref="outerOptionsRef"
            class="selected-options-content"
            :bordered="false"
            v-model:activeKey="activeKey"
            @change="initOpenedCollapsedSortable">
            <template #expandIcon="{ isActive }">
                <span>
                    <CaretRightOutlined class="custom-collapse-icon" :class="{'rotate-deg': isActive}"/>
                </span>
            </template>
            <a-collapse-panel v-for="(outerOption, outerIndex) of options"
                :data-code="outerOption.nodeCode"
                :key="outerOption.nodeCode">
                <template #header>
                    <div>
                        <span class="custom-icon-touch drag-icon"></span>
                        <div class="drag-item">
                            <span>{{ outerOption.nodeName }}</span>
                            <div @click.stop class="icon-box">
                                <span class="custom-icon-to-top to-top" @click="toTop(options, outerIndex)"></span>
                                <span v-if="!getChildOptionStatus(outerOption)" class="custom-icon-deletex del-icon" @click="del(options, outerIndex)"></span>
                            </div>
                        </div>
                    </div>
                </template>
                
                <div :class="'inner-option-container-' + outerOption.nodeCode">
                    <div v-for="(innerOption, innerIndex) of outerOption.sysExportNodeColumnList"
                        :data-code="innerOption.nodeCode"
                        class="inner-option-container"
                        :key="innerOption.nodeCode">
                        <div>
                            <span class="custom-icon-touch second-level-drag-icon"></span>
                            <span>{{ innerOption.nodeName }}</span>
                        </div>
                        <div class="icon-box">
                            <span class="custom-icon-to-top to-top" @click="toTop(outerOption.sysExportNodeColumnList, innerIndex)"></span>
                            <span v-if="!innerOption.disable" class="custom-icon-deletex del-icon"  @click="del(outerOption.sysExportNodeColumnList, innerIndex, outerIndex)"></span>
                        </div>
                    </div>
                </div>
            </a-collapse-panel>
        </a-collapse>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, unref, } from 'vue';
import Sortablejs from 'sortablejs';
import { CaretRightOutlined } from '@ant-design/icons-vue';
import { isNullAndUnDef } from '/@/utils/is';

const props = defineProps({
    options: {
        type: Array,
    },
    selectedCount: {
        type: Number,
    },
});
const emit = defineEmits(['update:options', 'update:selectedCount']);
const outerOptionsRef = ref<ComponentRef>(null);
const activeKey = ref([]);
const sortables = {};
const initSortable = (source, el, handleSelector, sortableId) => {
    sortables[sortableId]?.destroy(); // 排序与toTop更改顺序有排斥，所以先销毁
    sortables[sortableId] = Sortablejs.create(unref(el), {
        animation: 500,
        delay: 400,
        delayOnTouchOnly: true,
        handle: handleSelector,
        dataIdAttr: 'data-code', // 根据该属性记录生成的顺序数组， 在html中添加属性
        onEnd: (evt) => {
            const { oldIndex, newIndex } = evt;
            if (isNullAndUnDef(oldIndex) || isNullAndUnDef(newIndex) || oldIndex === newIndex) {
                return;
            }
            if (oldIndex > newIndex) {
                source.splice(newIndex, 0, source[oldIndex]);
                source.splice(oldIndex + 1, 1);
            } else {
                source.splice(newIndex + 1, 0, source[oldIndex]);
                source.splice(oldIndex, 1);
            }

            emitSelected()
        },
    });

    resetSortableOrder(sortableId, source)
}

const toTop = (source, index) => {
    const splicedItem = source.splice(index, 1)[0];
    source.unshift(splicedItem)
    emitSelected()
}

const del = (source, index, outerIndex?) => {
    source.splice(index, 1);
    if (source.length === 0 && outerIndex != null) { // 如果当前项清零，外层也要跟着清除
        del(props.options, outerIndex);
    }
    emitSelected()
}

const resetSortableOrder = (sortableId, source) => {
    const newOrders = source.map(item => item.nodeCode)
    sortables[sortableId]?.sort(newOrders);
}

const emitSelected = () => {
    const newOptions = [...props.options];
    emit('update:options', newOptions)
}

const initAllSortable = async () => {
    const tempActiveKeys = props.options.map(item => item.nodeCode);
    await nextTick();
    const outerOptionsEle = unref(outerOptionsRef);
    if (!outerOptionsEle) return;
    const el = outerOptionsEle.$el;
    initSortable(props.options, el, '.drag-icon', 'outerSortable')
    
    await nextTick();
    let currentSelectedCount = 0;
    props.options.forEach((item) => {
        if (!item.sysExportNodeColumnList?.length) {
            return;
        }
        currentSelectedCount += item.sysExportNodeColumnList.length
        initInnerSortable(el, item)
    })

    emit('update:selectedCount', currentSelectedCount)

    firstInited && (activeKey.value = tempActiveKeys);
    firstInited = false;
}

let firstInited = true;

// 新增的二级目录，如果没有初始展开，没有dom，在展开的时候需要用sortable做排序
const initOpenedCollapsedSortable = (activeKeys) => {
    const innerOpenedOptions = props.options.filter(item => activeKeys.includes(item.nodeCode))
    innerOpenedOptions.forEach((item => {
        const outerOptionsEle = unref(outerOptionsRef);
        const el = outerOptionsEle.$el;
        initInnerSortable(el, item);
    }))
}

const getChildOptionStatus = (option) => {
  return option.sysExportNodeColumnList?.some(item => item.disable)
}

const initInnerSortable = (el, item) => {
    if (!item.sysExportNodeColumnList?.length) {
        return;
    }
    // 要获取到dom，所以要给渲染时间
    setTimeout(() => {
        const innerEl = el.querySelector(`.inner-option-container-${item.nodeCode}`)
        if (!innerEl) {
            return;
        }
        initSortable(item.sysExportNodeColumnList, innerEl, '.second-level-drag-icon', item.nodeCode)
    }, 50)
}

const showSomeEle = (nodeCode) => {
    const outerOptionsEle = unref(outerOptionsRef);
    if (!outerOptionsEle) return;
    let targetNode = `*[data-code=${nodeCode}]`;;
    let blockPosition = 'start';
    if (Array.isArray(nodeCode)) { // 二级目录进入视角，需要传数组，包含一级nodeCode，用于collapse的展开
        const [innerNodeCode, outerNodeCode] = nodeCode;
        targetNode = `*[data-code=${outerNodeCode}] *[data-code=${innerNodeCode}]`; // 因为nodeCode可能重复，所以加外层nodeCode进行过滤筛选
        blockPosition = 'end';
        if (!activeKey.value.includes(outerNodeCode)) {
            activeKey.value = [...activeKey.value, outerNodeCode];
        }
    }
    // 等待dom及sortable、collapse的展开渲染
    setTimeout(() => {
        const targetNodeEle = outerOptionsEle.$el.querySelector(targetNode);
        targetNodeEle?.scrollIntoView({
            //滚动到指定节点
            block: blockPosition, //值有start,center,end，nearest，当前显示在视图区域中间
            behavior: 'auto', //值有auto、instant,smooth，缓动动画（当前是慢速的）
        })
    }, 500)
    
}

watch(() => props.options, () => {
    if (!props.options?.length) {
        emit('update:selectedCount', 0)
        return;
    }
    initAllSortable()
})

defineExpose({
    showSomeEle,
})
</script>

<style lang="less" scoped>
.node-name {
    color: #262626;
    font-size: 14px;
}
.drag-item {
    display: flex;
    justify-content: space-between;
}
.to-top {
    margin-right: 12px;
}
.del-icon, .to-top {
    cursor: pointer;
    color: #999;
    font-size: 14px;
    &:hover {
        color: #2c68ff;
    }
}
.inner-option-container {
    display: flex;
    justify-content: space-between;
    border-radius: 2px;
    &:hover {
        background-color: rgba(44, 104, 255, 0.06);
    }
}
.drag-icon {
    position: absolute;
    left: -20px;
}
.second-level-drag-icon {
    cursor: move;
}
.custom-icon-touch {
    margin-right: 12px;
}
.export-modal-title {
    color: #262626;
    font-size: 16px;
    line-height: 26px;
}
.selected-options-content {
    max-height: calc(100% - 26px);
    overflow-y: auto
}
.selected-sort-container {
    height: 100%;
}
.rotate-deg {
    transform: rotate(90deg);
}
.custom-collapse-icon {
    transition: all 0.3s ease-in-out;
}
.icon-box{
  width: 40px;
}
</style>